import React, { useEffect, useState } from 'react';
import { fetchUserByWallet } from './userUtils';
import ssalsoongiIcon from '../assets/ssalsoongi_icon.png';
import { supabase } from '../supabaseClient';
import RaidGame from './RaidGame';

export default function RaidModal({ open, onClose }) {
  const [totalSsaltaforce, setTotalSsaltaforce] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeBoss, setActiveBoss] = useState(null);
  const [bosses, setBosses] = useState([]);
  const [bossLoading, setBossLoading] = useState(true);
  const [bossError, setBossError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchUserData();
      fetchBosses();
    }
  }, [open]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const data = await fetchUserByWallet();
      setTotalSsaltaforce(data?.total_ssaltaforce ?? 0);
    } catch (error) {
      console.error('Failed to fetch user data', error);
      setTotalSsaltaforce(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchBosses = async () => {
    setBossLoading(true);
    setBossError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Login required');
      }

      const response = await supabase.functions.invoke('get-boss-data', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch bosses');
      }

      const data = response.data || [];

      const normalized = data.map((item) => ({
        id: item.id,
        name: item.name,
        requiredSsaltaforce: item.requiredSsaltaforce ?? 0,
        image: item.image || ssalsoongiIcon,
        maxEntry: item.maxEntry ?? 1,
        remaining: item.remaining ?? item.maxEntry ?? 0,
      }));

      setBosses(normalized);
    } catch (err) {
      console.error('Error fetching bosses:', err);
      setBossError(err.message);
      setBosses([]);
    } finally {
      setBossLoading(false);
    }
  };

  const handleEnter = async (boss) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        alert('Login required.');
        return;
      }

      // 서버에서 계산된 remaining 기준으로 선제 체크
      const remaining = boss.remaining ?? boss.maxEntry;
      if (remaining <= 0) {
        alert('Entry limit reached for today.');
        return;
      }

      const { data, error } = await supabase.functions.invoke('enter-raid', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          bossId: boss.id,
        },
      });

      if (error) {
        console.error('enter-raid error:', error);
        alert('Failed to enter raid. Please try again.');
        return;
      }

      // enter-raid 안에서도 일일 제한을 체크하니,
      // 필요하다면 data.success / data.error를 추가로 확인해서 처리 가능
      setActiveBoss(boss);
    } catch (e) {
      console.error('Failed to call enter-raid:', e);
      alert('Failed to enter raid. Please try again.');
    }
  };

  const handleGameEnd = async (result) => {
    // 승패/무승부 등 결과가 나왔으면 서버에서 남은 입장 횟수를 다시 가져와 최신화
    if (result === 'win' || result === 'lose' || result === 'draw') {
      await fetchBosses();
    }
    setActiveBoss(null);
  };

  if (!open) return null;

  const isAnyLoading = loading || bossLoading;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '90%',
          maxWidth: 420,
          background: '#fff',
          borderRadius: 24,
          padding: '32px 20px',
          position: 'relative',
          textAlign: 'center',
          maxHeight: '90vh',
          overflowY: 'auto',
          minHeight: 500,
          display: 'flex',
          flexDirection: 'column',
          padding: activeBoss ? 0 : '32px 20px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            border: 'none',
            background: 'transparent',
            fontSize: 24,
            cursor: 'pointer',
            zIndex: 10,
          }}
        >
          ✕
        </button>

        {activeBoss ? (
          <RaidGame
            boss={activeBoss}
            onClose={() => setActiveBoss(null)}
            onGameEnd={handleGameEnd}
          />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: 20,
                position: 'relative',
              }}
            >
              <h2 style={{ margin: 0 }}>Boss Raid</h2>
            </div>

            {isAnyLoading ? (
              <p>Loading...</p>
            ) : bosses.length === 0 ? (
              <p>{bossError ? `Error: ${bossError}` : 'No bosses available.'}</p>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                }}
              >
                {bosses.map((boss) => {
                  const canEnter =
                    (totalSsaltaforce ?? 0) >= boss.requiredSsaltaforce;
                  const remaining = boss.remaining ?? boss.maxEntry;
                  const isLimitReached = remaining === 0;

                  return (
                    <div
                      key={boss.id}
                      style={{
                        borderRadius: 12,
                        padding: 0,
                        height: 140,
                        backgroundColor: '#f9f9f9',
                        backgroundImage: `url(${boss.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 10,
                          left: 12,
                          padding: '6px 10px',
                          borderRadius: 8,
                          background: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          textAlign: 'left',
                          maxWidth: '70%',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 16,
                            marginBottom: 4,
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                          }}
                        >
                          {boss.name}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: '#e0e0e0',
                          }}
                        >
                          Required PetForce:{' '}
                          <span
                            style={{
                              fontWeight: 'bold',
                              color: canEnter ? '#8cff8c' : '#ff8c8c',
                            }}
                          >
                            {boss.requiredSsaltaforce}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          position: 'absolute',
                          left: 12,
                          bottom: 12,
                        }}
                      >
                        {!canEnter && (
                          <div
                            style={{
                              marginBottom: 6,
                              fontSize: 12,
                              color: '#ffcccc',
                              textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                            }}
                          >
                            Not enough PetForce!
                          </div>
                        )}
                        <button
                          disabled={!canEnter || isLimitReached}
                          onClick={() => handleEnter(boss)}
                          style={{
                            padding: '8px 18px',
                            borderRadius: 999,
                            border: 'none',
                            background:
                              !canEnter || isLimitReached
                                ? 'rgba(200,200,200,0.9)'
                                : 'rgba(79,195,212,0.95)',
                            color:
                              !canEnter || isLimitReached ? '#555' : '#fff',
                            fontWeight: 700,
                            fontSize: 13,
                            cursor:
                              !canEnter || isLimitReached
                                ? 'not-allowed'
                                : 'pointer',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                          }}
                        >
                          {isLimitReached
                            ? 'Limit Reached'
                            : `Enter (${remaining}/${boss.maxEntry})`}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
